import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AccessoriesViewService,
  BreakpointService,
  LocalStorageService,
  UserTypeService,
} from '@boels/helpers';
import {
  AccordionMode,
  BoelsProductReference,
  BoelsUserTypeEnum,
  BOProduct,
  StorageKey,
  UnitPrice,
} from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { AccordionItemComponent } from '../accordion/accordion-item.component';
import { AccordionComponent } from '../accordion/accordion.component';
import { PriceService } from '../product-price/price.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'boels-accessories-view',
  templateUrl: './accessories-view.component.html',
  styleUrls: ['./accessories-view.component.scss'],
  imports: [
    TranslocoModule,
    FontAwesomeModule,
    AccordionComponent,
    AccordionItemComponent,
    NgbTooltipModule,
  ],
  providers: [
    AccessoriesViewService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productDetails'],
    },
  ],
})
export class AccessoriesViewComponent implements OnInit, OnDestroy {
  priceService = inject(PriceService);
  private accessoriesViewService = inject(AccessoriesViewService);
  private cdRef = inject(ChangeDetectorRef);
  private breakpointService = inject(BreakpointService);
  private localStorageService = inject(LocalStorageService);
  private destroyRef = inject(DestroyRef);

  @Input() accessories: BoelsProductReference[];
  @Input() label: string;
  @Input() sublabel: string;
  @Input() addToCartForm: FormGroup | undefined;
  @Input() readOnlyInputs = false;
  @Input() pdpView = false;
  @Input() accordionMode = AccordionMode.SINGLE;
  private subs = new Subscription();
  private maxNumberOfPriceTags = 0;
  private readonly priceTagWidth = 100;
  priceLabel: string = this.priceService.getVatLabel();
  productTagsGridStyle: string;
  summaryPadding: string;
  userType: BoelsUserTypeEnum;
  userTypeService = inject(UserTypeService);

  faChevronDown = faChevronDown;
  faCircleInfo = faCircleInfo;

  constructor() {
    this.userType = this.localStorageService.getItem(StorageKey.USER_TYPE);
  }

  ngOnInit(): void {
    this.subs.add(
      this.userTypeService
        .onUserTypeChange()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.priceLabel = this.priceService.getVatLabel();
          this.cdRef.detectChanges();
        }),
    );

    // use shared number of price tags
    this.subs.add(
      this.accessoriesViewService.numberOfProductTags$.subscribe(
        (value: number) => {
          this.maxNumberOfPriceTags = value;
          if (value > 0) {
            this.productTagsGridStyle = this.priceTagWidth + 'px';
            for (let i = 0; i < value; i++) {
              this.productTagsGridStyle += ' ' + this.priceTagWidth + 'px';
            }
            this.cdRef.detectChanges();
          }
        },
      ),
    );
    this.getSummaryPadding();

    this.userType = this.localStorageService.getItem(StorageKey.USER_TYPE);
  }

  ngOnDestroy(): void {
    this.accessoriesViewService.setNumberOfAccessories(0);
    this.cdRef.detectChanges();
    this.subs.unsubscribe();
  }

  getUnitPrices(product: BOProduct): UnitPrice[] {
    const accessoryPrices = this.priceService.getPricesForUserType(product);
    if (accessoryPrices.length > this.maxNumberOfPriceTags) {
      this.accessoriesViewService.setNumberOfAccessories(
        accessoryPrices.length,
      );
    }

    return accessoryPrices;
  }

  getSummaryPadding(): void {
    this.subs.add(
      combineLatest([
        this.breakpointService.mdAndBelow$,
        this.breakpointService.smAndBelow$,
      ])
        .pipe(
          distinctUntilChanged(),
          switchMap(([mdAndDown, smAndDown]: [boolean, boolean]) => {
            if (this.pdpView && !mdAndDown && !smAndDown) {
              return this.accessoriesViewService.numberOfProductTags$.pipe(
                map((numTags) => {
                  const space = 20;
                  let padding = this.priceTagWidth * numTags + space;
                  padding =
                    padding > space ? padding + this.priceTagWidth : padding; // add extra padding for prices tag
                  return padding + 'px';
                }),
              );
            }
            return of('0');
          }),
        )
        .subscribe((value) => {
          this.summaryPadding = value;
          this.cdRef.detectChanges();
        }),
    );
  }
}
